import type { ReactNode } from 'react';

import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import Card from '../Card/Card';
import CopyButton from '../CopyButton/CopyButton';

interface PickCardProps {
  /**
   * Card title
   */
  title?: string;
  /**
   * Card description under title
   */
  description?: ReactNode;
  /**
   * Card image URL
   */
  imageUrl?: string;
  /**
   * If card image is loading
   */
  imageUrlLoading?: boolean;
  /**
   * On card click callback
   */
  onClick?: () => void;
  /**
   * Card image overlay
   */
  imageOverlay?: ReactNode;
  /**
   * Card image container
   */
  thumbnail?: ReactNode;
  /**
   * Additional card classes
   */
  additionalContainerClasses?: string;
  /**
   * Additional image classes
   */
  additionalImageClasses?: string;
  /**
   * Relations card test id
   */
  testId?: string;
}

const PickCard = ({
  title = '',
  description = '',
  imageUrl = '',
  imageUrlLoading = false,
  thumbnail = '',
  imageOverlay = '',
  onClick,
  additionalContainerClasses = '',
  additionalImageClasses = '',
  testId = '',
}: PickCardProps) => {
  const { t } = useTranslation();

  return (
    <Card
      onClick={onClick}
      body={
        <>
          <div
            className="dark:text-slate-200 pointer-events-auto"
            title={title}
          >
            <CopyButton
              text={title}
              onCopy={() => {
                toast.success(t('ObjectsOfType.Copied', { text: title }));
              }}
              {...getTestProps(testId, `copy-title`, 'testId')}
            >
              {title}
            </CopyButton>
          </div>
          <div className="text-sm text-slate-400 mt-auto">{description}</div>
        </>
      }
      imageUrl={imageUrl}
      imageUrlLoading={imageUrlLoading}
      thumbnail={thumbnail}
      imageOverlay={imageOverlay}
      additionalContainerClasses={twMerge(
        'bg-slate-50 border border-slate-100 dark:bg-slate-800 dark:border-slate-700 w-full h-full flex',
        additionalContainerClasses,
      )}
      additionalImageContainerClasses="shrink-0 h-40 xs:h-28 w-full bg-white dark:bg-slate-700"
      additionalBodyClasses="px-3 py-2.5 flex flex-col gap-4 h-full w-full wrap-break-word"
      additionalImageClasses={additionalImageClasses}
      {...getTestProps(testId, '', 'testId')}
    />
  );
};

export default PickCard;
